import { useControllableValue, useMemoizedFn } from "ahooks";
import { Cascader, type CascaderProps } from "antd";
import classNames from "classnames";
import { first, flatten, isEqual, last } from "lodash";
import { useRef, useState } from "react";
import HBIcon from "../HBIcon";
import styles from "./index.module.less";

/**
 * 级联选择器
 */
export function HBCascader<T>(
  props: CascaderProps<T> & {
    /**最多选择数量 */
    maxCount?: number;
  }
) {
  const {
    fieldNames = {
      label: "label",
      value: "value",
      children: "children",
    },
    multiple,
    options,
    maxCount,
    ...rest
  } = props;
  const [value, setValue] = useControllableValue<any[]>(props);

  const [activeKeys, setActiveKeys] = useState<any[]>([]);
  const ref = useRef<any>();

  const onItemClick = useMemoizedFn((item, isTop) => {
    const value = item[fieldNames.value];

    if (!item[fieldNames.children]) {
      const arr = activeKeys.map((item) => item[fieldNames.value]);
      const selected = [...arr, value];
      if (multiple) {
        setValue((prev) => {
          if (prev?.find((v) => isEqual(v, selected))) {
            return prev.filter((v) => !isEqual(v, selected));
          }
          if (prev.length >= maxCount) {
            return prev;
          }
          return [...(prev || []), selected];
        });
      } else {
        // 失去焦点，隐藏下拉面板
        ref.current.blur();
        setValue(selected);
      }

      return;
    }

    if (isTop) {
      setActiveKeys([item]);
      return;
    }

    setActiveKeys([...activeKeys, item]);
  });

  const renderItem = useMemoizedFn((item, key, levelIndex) => {
    const isActive = activeKeys[levelIndex] === item;

    const isSelected = multiple
      ? flatten(value).includes(item[fieldNames.value])
      : last(value) === item[fieldNames.value];

    return (
      <div
        className={classNames(
          styles.item,
          isActive && styles.active,
          isSelected && styles.isSelected
        )}
        key={key}
        onClick={() => {
          onItemClick(item, levelIndex === 0);
        }}
      >
        {item[fieldNames.label]}
        {item[fieldNames.children] ? (
          <HBIcon className={styles.rightIcon} type={"ic_chevron_right"} />
        ) : (
          isSelected && (
            <HBIcon className={styles.rightIcon} type={"ic_check"} />
          )
        )}
      </div>
    );
  });

  const setDefaultActiveKey = useMemoizedFn(() => {
    // 根据value设置activeKey
    let defaultValue = value;
    if (multiple) {
      defaultValue = last(value || []);
    }

    if (!defaultValue?.length) {
      // 没有值，默认展开第一项
      return setActiveKeys(options?.length > 0 ? [first(options)] : []);
    }

    const res = [];

    function findActiveNode(nodes, value) {
      const node = nodes.find(
        (option) => option[fieldNames.value] === value[0]
      );

      if (node[fieldNames.children]) {
        res.push(node);
        findActiveNode(node[fieldNames.children], value.slice(1));
      }
    }

    findActiveNode(options, [...defaultValue]);

    setActiveKeys(res);
  });

  return (
    <Cascader
      className={styles.select}
      options={options}
      multiple={multiple}
      value={value}
      suffixIcon={
        <HBIcon type="ic_arrow_down" className={styles.selectArrow} />
      }
      getPopupContainer={(trigger) => trigger.parentNode}
      onClear={() => {
        setValue([]);
      }}
      ref={ref}
      showArrow
      dropdownRender={(menu) => {
        if (!options?.length) {
          // 展示默认的空内容
          return menu;
        }

        return (
          <div className={styles.dropdownContent}>
            <div className={styles.topLevel}>
              {options.map((item, index) => renderItem(item, index, 0))}
            </div>
            {activeKeys.map((level, levelIndex) => {
              return (
                <div className={styles.nextLevel} key={levelIndex}>
                  {level[fieldNames.children].map((item, index) =>
                    renderItem(item, index, levelIndex + 1)
                  )}
                </div>
              );
            })}
          </div>
        );
      }}
      onDropdownVisibleChange={() => {
        setDefaultActiveKey();
      }}
      {...rest}
    />
  );
}
